import React, { useState, useEffect, useCallback } from "react";
import "./MyDiscipline.css";
import { getMyAttendanceMonth, getMyAttendanceYear } from "../../../../api/api";

const MyDiscipline = () => {
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [monthlyData, setMonthlyData] = useState(null);
  const [yearlyData, setYearlyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Функция для сравнения времени в формате "HH:MM"
  const compareTime = (time1, time2) => {
    if (!time1 || !time2) return null;
    const [h1, m1] = time1.split(":").map(Number);
    const [h2, m2] = time2.split(":").map(Number);
    const minutes1 = h1 * 60 + m1;
    const minutes2 = h2 * 60 + m2;
    if (minutes1 < minutes2) return -1; // time1 раньше time2
    if (minutes1 > minutes2) return 1; // time1 позже time2
    return 0; // равны
  };

  // Функция для преобразования данных API в формат компонента
  const transformApiData = useCallback((apiData, period) => {
    if (!apiData) return null;

    if (period === "month") {
      // Для месячных данных
      const days = apiData.days || [];
      const monthlyData = days.map((day) => {
        const dayNumber = new Date(day.work_date).getDate();
        // Преобразуем время опоздания из формата "HH:MM" в минуты
        let delayMinutes = 0;
        if (day.late_hours_minutes && day.late_hours_minutes !== "00:00") {
          const [hours, minutes] = day.late_hours_minutes
            .split(":")
            .map(Number);
          delayMinutes = hours * 60 + minutes;
        }
        return {
          day: dayNumber,
          delay: delayMinutes,
        };
      });

      const tableData = days.map((day) => {
        const date = new Date(day.work_date);
        const isWeekend = !day.workday_flag;
        const hasDelay =
          day.late_hours_minutes && day.late_hours_minutes !== "00:00";
        const hasMismatch = day.time_diff && !day.time_diff.startsWith("0:00");

        // Проверяем, пришел ли сотрудник вовремя или раньше
        const isOnTimeOrEarly =
          !isWeekend &&
          day.first_entry &&
          day.work_start_time &&
          compareTime(day.first_entry, day.work_start_time) <= 0;

        // Определяем delayGroup и arrivalCategory
        let delayGroup = "-";
        let arrivalCategory = "Вовремя";

        if (!isWeekend) {
          if (isOnTimeOrEarly || !hasDelay) {
            delayGroup = "-";
            arrivalCategory = day.late_type || "Вовремя";
          } else if (hasDelay) {
            const lateMinutes =
              parseInt(day.late_hours_minutes.split(":")[0]) * 60 +
              parseInt(day.late_hours_minutes.split(":")[1]);
            delayGroup = lateMinutes <= 10 ? "0-10" : "10-30";
            arrivalCategory =
              day.late_type ||
              (lateMinutes <= 10 ? "Незначительное опоздание" : "Опаздание");
          }
        }

        return {
          date: date.toLocaleDateString("ru-RU"),
          scheduledStart:
            day.work_start_time || (isWeekend ? "Выходной день" : "9:00"),
          actualArrival: day.first_entry || (isWeekend ? "Выходной день" : "-"),
          delayGroup: delayGroup,
          arrivalCategory: arrivalCategory,
          departure: day.last_exit || (isWeekend ? "Выходной день" : "-"),
          faceIdTime: isWeekend ? "Выходной день" : day.office_time || "0:00",
          timesheetTime: day.report_time_spent || "0:00",
          difference: day.time_diff || "0:00",
          comment: day.comment || "Все в порядке",
          isWeekend: isWeekend,
          isGood: !hasDelay && !hasMismatch,
          hasIssue: hasDelay || hasMismatch,
        };
      });

      return {
        monthlyData: { month: monthlyData },
        tableData: { month: tableData },
        summary: {
          month: {
            daysWithoutDelay: apiData.intime_days_count || 0,
            daysWithDelay: apiData.late_days_count || 0,
            totalDays:
              (apiData.intime_days_count || 0) + (apiData.late_days_count || 0),
            intimePercentage: apiData.intime_percentage || 0,
          },
        },
      };
    } else {
      // Для 6 месяцев и года
      const months = apiData.months || [];
      const monthlyData = months.map((month) => ({
        month: month.month,
        delays: month.lateness_count || 0,
      }));

      const tableData = months.map((month) => ({
        date: month.month,
        scheduledStart: "9:00",
        actualArrival: month.lateness_count > 0 ? "Опоздание" : "Вовремя",
        delayGroup: month.lateness_count > 0 ? "0-10" : "0-10",
        departure: "18:00",
        faceIdTime: `${month.workdays_count || 0} дней`,
        timesheetTime: `${month.workdays_count || 0} дней`,
        difference: month.average_lateness
          ? `${month.average_lateness} мин`
          : "0 мин",
        comment:
          month.report_mismatches_count > 0
            ? `Несоответствий: ${month.report_mismatches_count}`
            : "Все в порядке",
        isWeekend: false,
        isGood: month.lateness_count === 0,
        hasIssue: month.lateness_count > 0 || month.report_mismatches_count > 0,
      }));

      return {
        monthlyData: { [period]: monthlyData },
        tableData: { [period]: tableData },
        summary: {
          [period]: {
            daysWithoutDelay: apiData.total_intime_count || 0,
            daysWithDelay:
              (apiData.total_workdays_count || 0) -
              (apiData.total_intime_count || 0),
            totalDays: apiData.total_workdays_count || 0,
            intimePercentage: apiData.intime_percent || 0,
          },
        },
      };
    }
  }, []);

  const fetchMonthlyData = useCallback(
    async (date = selectedMonth) => {
      try {
        setLoading(true);
        setError(null);

        const year = date.getFullYear();
        const month = date.getMonth() + 1;

        const response = await getMyAttendanceMonth(year, month);
        const transformedData = transformApiData(response, "month");
        setMonthlyData(transformedData);
      } catch (err) {
        console.error("Ошибка при загрузке месячных данных:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [transformApiData, selectedMonth]
  );

  const fetchYearlyData = useCallback(
    async (year = selectedYear) => {
      try {
        const response = await getMyAttendanceYear(year);
        const transformedData = transformApiData(response, "year");
        setYearlyData(transformedData);
      } catch (err) {
        console.error("Ошибка при загрузке годовых данных:", err);
        setError(err.message);
      }
    },
    [transformApiData, selectedYear]
  );

  useEffect(() => {
    fetchMonthlyData();
    fetchYearlyData();
  }, [fetchMonthlyData, fetchYearlyData]);

  // Обработка состояний загрузки и ошибок
  if (loading) {
    return (
      <div className="my-discipline-container">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <div>Загрузка данных...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-discipline-container">
        <div style={{ textAlign: "center", padding: "50px", color: "red" }}>
          <div>Ошибка загрузки данных: {error}</div>
          <button
            onClick={() => {
              fetchMonthlyData();
              fetchYearlyData();
            }}
            style={{ marginTop: "20px", padding: "10px 20px" }}
          >
            Попробовать снова
          </button>
        </div>
      </div>
    );
  }

  if (!monthlyData && !yearlyData) {
    return (
      <div className="my-discipline-container">
        <div style={{ textAlign: "center", padding: "50px" }}>
          <div>Нет данных для отображения</div>
        </div>
      </div>
    );
  }

  const handleMonthChange = (direction) => {
    const newMonth = new Date(selectedMonth);
    if (direction === "prev") {
      newMonth.setMonth(newMonth.getMonth() - 1);
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1);
    }
    setSelectedMonth(newMonth);
    fetchMonthlyData(newMonth);
  };

  const handleYearChange = (direction) => {
    const newYear = direction === "prev" ? selectedYear - 1 : selectedYear + 1;
    setSelectedYear(newYear);
    fetchYearlyData(newYear);
  };

  const formatMonthYear = (date) => {
    const months = [
      "Январь",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  // Получение статистики для карточек
  const getStatistics = () => {
    if (!monthlyData || !monthlyData.summary || !monthlyData.summary.month) {
      return {
        daysWithoutDelay: 0,
        daysWithDelay: 0,
        intimePercentage: 0,
        totalDays: 0,
      };
    }
    return monthlyData.summary.month;
  };

  // Получение мотивационного сообщения
  const getMotivationalMessage = () => {
    const stats = getStatistics();
    const { daysWithDelay } = stats;

    if (daysWithDelay === 0) {
      return {
        type: "success",
        title: "Вы стабильно приходите вовремя!",
        message: "Благодаря вам, команда движется в правильном ритме.",
        icon: "✓",
      };
    } else if (daysWithDelay <= 2) {
      return {
        type: "info",
        title: "Хорошая дисциплина!",
        message: "Продолжайте в том же духе.",
        icon: "👍",
      };
    } else if (daysWithDelay <= 4) {
      return {
        type: "warning",
        title: `Уже ${daysWithDelay} опозданий - стоит обратить внимание.`,
        message: "Ваши опоздания влияют на продуктивность команды.",
        icon: "⚠",
      };
    } else {
      return {
        type: "error",
        title: `Уже ${daysWithDelay} опозданий - критическая ситуация!`,
        message: "Необходимо срочно улучшить дисциплину.",
        icon: "🚨",
      };
    }
  };

  // Рендер статистических карточек
  const renderStatisticsCards = () => {
    const stats = getStatistics();

    return (
      <div className="statistics-cards">
        <div className="stat-card">
          <div className="stat-icon stat-icon--target">🎯</div>
          <div className="stat-content">
            <div className="stat-value">{stats.intimePercentage}%</div>
            <div className="stat-label">дней без опозданий</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon--success">✓</div>
          <div className="stat-content">
            <div className="stat-value">{stats.daysWithoutDelay}</div>
            <div className="stat-label">пунктуальные дни</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon stat-icon--warning">⚠</div>
          <div className="stat-content">
            <div className="stat-value">{stats.daysWithDelay}</div>
            <div className="stat-label">дни с опозданием</div>
          </div>
        </div>
      </div>
    );
  };

  // Рендер мотивационного сообщения
  const renderMotivationalMessage = () => {
    const message = getMotivationalMessage();

    return (
      <div
        className={`motivational-message motivational-message--${message.type}`}
      >
        <div className="motivational-icon">{message.icon}</div>
        <div className="motivational-content">
          <div className="motivational-title">{message.title}</div>
          <div className="motivational-text">{message.message}</div>
        </div>
      </div>
    );
  };

  return (
    <div className="my-discipline-container">
      {/* Заголовок страницы */}
      <div className="page-header">
        <h1 className="page-title">Моя дисциплина</h1>
      </div>

      {/* Статистические карточки */}
      {renderStatisticsCards()}

      {/* Информация о данных */}
      <div className="data-info">
        <span>
          Данные за {formatMonthYear(selectedMonth)} (обновлены на{" "}
          {new Date().getDate()}.{new Date().getMonth() + 1})
        </span>
        {/* <button className="refresh-btn">🔄</button> */}
      </div>

      {/* Мотивационное сообщение */}
      {renderMotivationalMessage()}

      {/* Месячная таблица */}
      <div className="monthly-section">
        <div className="section-header">
          <h2 className="section-title">Статистика за месяц</h2>
          <div className="month-navigation">
            <button
              className="nav-button"
              onClick={() => handleMonthChange("prev")}
            >
              ←
            </button>
            <span className="current-month">
              {formatMonthYear(selectedMonth)}
            </span>
            <button
              className="nav-button"
              onClick={() => handleMonthChange("next")}
            >
              →
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="discipline-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Начало дня по графику</th>
                <th>Фактическое время прихода</th>
                <th>Группа опозданий</th>
                <th>Категория прихода</th>
                <th>Время ухода</th>
                <th>Фактическое время в офисе</th>
                <th>Время в офисе по табелю</th>
                <th>Отклонение по табелю</th>
                <th>Комментарий</th>
              </tr>
            </thead>
            <tbody>
              {monthlyData &&
                monthlyData.tableData &&
                monthlyData.tableData.month &&
                monthlyData.tableData.month.map((row, index) => (
                  <tr
                    key={index}
                    className={row.isWeekend ? "weekend-row" : ""}
                  >
                    <td>{row.date}</td>
                    <td>{row.scheduledStart}</td>
                    <td>{row.actualArrival}</td>
                    <td>
                      {row.delayGroup !== "-" && (
                        <div
                          className={`delay-group delay-group--${
                            row.delayGroup === "0-10" ? "yellow" : "orange"
                          }`}
                        >
                          {row.delayGroup}
                        </div>
                      )}
                      {row.delayGroup === "-" && "-"}
                    </td>
                    <td>
                      {row.delayGroup === "-"
                        ? row.arrivalCategory || "Вовремя"
                        : row.delayGroup === "0-10"
                        ? "Незначительное опоздание"
                        : row.delayGroup === "10-30"
                        ? "Опаздание"
                        : row.arrivalCategory || "-"}
                    </td>
                    <td>{row.departure}</td>
                    <td>{row.faceIdTime}</td>
                    <td>{row.timesheetTime}</td>
                    <td
                      className={
                        row.difference?.startsWith("-")
                          ? "negative-difference"
                          : ""
                      }
                    >
                      {row.difference}
                    </td>
                    <td>
                      <div className="comment-cell">
                        {row.isGood && (
                          <span className="status-icon status-icon--good">
                            ✓
                          </span>
                        )}
                        {row.hasIssue && (
                          <span className="status-icon status-icon--warning">
                            ⚠
                          </span>
                        )}
                        {row.comment}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Годовая таблица */}
      <div className="yearly-section">
        <div className="section-header">
          <h2 className="section-title">Годовая статистика</h2>
          <div className="year-navigation">
            <button
              className="nav-button"
              onClick={() => handleYearChange("prev")}
            >
              ←
            </button>
            <span className="current-year">{selectedYear}</span>
            <button
              className="nav-button"
              onClick={() => handleYearChange("next")}
            >
              →
            </button>
          </div>
        </div>

        <div className="table-container">
          <table className="discipline-table">
            <thead>
              <tr>
                <th>Месяц</th>
                <th>Рабочих дней</th>
                <th>Всего опозданий</th>
                <th>Среднее время опоздания</th>
                <th>% без опозданий</th>
                <th>Количество дней с несоответствием табелю</th>
              </tr>
            </thead>
            <tbody>
              {yearlyData &&
                yearlyData.tableData &&
                yearlyData.tableData.year &&
                yearlyData.tableData.year.map((row, index) => (
                  <tr key={index}>
                    <td>{row.date}</td>
                    <td>{row.timesheetTime}</td>
                    <td>{row.actualArrival === "Опоздание" ? "1" : "0"}</td>
                    <td>{row.difference}</td>
                    <td>{row.actualArrival === "Опоздание" ? "0%" : "100%"}</td>
                    <td>
                      {row.comment.includes("Несоответствий")
                        ? row.comment.split(": ")[1]
                        : "0"}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MyDiscipline;
